import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import MaterialIcon from '../ui/MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'

type SlidePagePanelProps = {
  open: boolean
  onClose: () => void
  /** When set, the header back button calls this instead of closing the panel. */
  onBack?: () => void
  title: string
  children: ReactNode
}

const SlidePagePanel = ({ open, onClose, onBack, title, children }: SlidePagePanelProps) => {
  const { t } = useTranslation()
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      setVisible(false)
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true))
      })
      return () => cancelAnimationFrame(id)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 350)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!mounted) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mounted])

  if (!mounted) return null

  return createPortal(
    <div
      className={`slide-page-panel fixed inset-0 z-[70] flex h-[100dvh] min-h-0 flex-col ${KILO.bg.page} ${
        visible ? 'slide-page-panel--open' : 'slide-page-panel--closed'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <header className="relative z-10 flex shrink-0 items-center justify-center border-b border-kilo-border bg-kilo-page px-4 py-3.5">
        <button
          type="button"
          onClick={onBack ?? onClose}
          className="absolute left-4 flex items-center gap-1 rounded-full px-2 py-1.5 text-sm font-medium text-kilo-text transition-colors hover:bg-kilo-nested"
        >
          <MaterialIcon name="arrow_back" size="sm" />
          {t('common.back')}
        </button>
        <h1 className="max-w-[min(100%,14rem)] px-16 text-center text-base font-bold tracking-tight text-kilo-text">
          {title}
        </h1>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="mx-auto flex w-full max-w-lg flex-col gap-4 px-4 py-5 pb-8">{children}</div>
      </div>
    </div>,
    document.body,
  )
}

export default SlidePagePanel
